import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import "./ViewportDialog.css";

/**
 * Viewport-attached dialog shell.
 *
 * `.screen` and `.kpi-screen` finish their entrance animation with
 * `transform: translateY(0)` (`animation-fill-mode: forwards`). That element
 * becomes the containing block for `position: fixed` descendants, so a dialog
 * rendered inside a long page opens at the top of the document instead of the
 * visible viewport. Portaling to `document.body` escapes that containing block.
 *
 * `presentation="sheet"` matches Record Payment: a bottom sheet on phones and
 * a centered dialog from 1024px. `presentation="center"` stays a centered
 * dialog at every width (confirms and short alerts).
 *
 * `unstyled` keeps a caller's existing overlay/panel classes (Record Payment)
 * while still portaling, locking scroll, and handling backdrop/Escape.
 */
export function ViewportDialog({
  open = true,
  onClose,
  children,
  presentation = "center",
  titleId,
  label,
  closeOnBackdrop = true,
  closeOnEscape = true,
  busy = false,
  unstyled = false,
  panelClassName = "",
  panelStyle,
  overlayClassName = "",
  zIndex,
}) {
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open || !closeOnEscape) return undefined;

    function onKeyDown(event) {
      if (event.key === "Escape" && !busy) onClose?.();
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, closeOnEscape, busy, onClose]);

  if (!open || typeof document === "undefined") return null;

  const sheet = presentation === "sheet";
  const overlayClass = unstyled
    ? overlayClassName
    : ["cp-vd-overlay", sheet ? "cp-vd-overlay--sheet" : "cp-vd-overlay--center", overlayClassName]
        .filter(Boolean)
        .join(" ");
  const panelClass = unstyled
    ? panelClassName
    : ["cp-vd-panel", panelClassName].filter(Boolean).join(" ");

  return createPortal(
    <div
      className={overlayClass}
      style={zIndex != null ? { zIndex } : undefined}
      onClick={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget && !busy) {
          onClose?.();
        }
      }}
    >
      <div
        className={panelClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-label={titleId ? undefined : label}
        style={panelStyle}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Move an existing overlay onto `document.body` and lock background scroll
 * without restyling it. Use when the overlay already has the right
 * presentation (centered confirm, full-screen detail, existing sheet chrome).
 */
export function ViewportPortal({ open = true, children }) {
  useBodyScrollLock(open);
  if (!open || typeof document === "undefined") return null;
  return createPortal(children, document.body);
}
